import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  Link,
} from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import AccountNotFoundDialog from './AccountNotFoundDialog';
import TermsOfServiceDialog from './TermsOfServiceDialog';
import PrivacyPolicyDialog from './PrivacyPolicyDialog';
import ForgotPasswordDialog from './ForgotPasswordDialog';

// Global variable to persist across component re-mounts
let globalShowDialog = false;

interface LoginFormProps {
  onToggleMode: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onToggleMode }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Check platform immediately (not in useEffect to avoid render delay)
  const platform = Capacitor.getPlatform();
  const isWeb = platform === 'web';
  
  // Log platform for debugging - MULTIPLE LOGS TO ENSURE VISIBILITY
  console.log('🔵🔵🔵 LoginForm RENDERED - Platform:', platform, 'isWeb:', isWeb, '🔵🔵🔵');
  useEffect(() => {
    console.log('🔵 LoginForm useEffect - Platform detected:', platform, 'isWeb:', isWeb);
    console.log('🔵 LoginForm - Google Sign-In button will be:', isWeb ? 'VISIBLE' : 'HIDDEN');
  }, [platform, isWeb]);
  
  const [showAccountNotFoundDialog, setShowAccountNotFoundDialog] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [showPrivacyDialog, setShowPrivacyDialog] = useState(false);
  const [showForgotPasswordDialog, setShowForgotPasswordDialog] = useState(false);
  const { signIn, signInWithGoogle, signInWithGoogleRedirect } = useAuth();
  const navigate = useNavigate();

  // Check global flag on every render
  useEffect(() => {
    if (globalShowDialog && !showAccountNotFoundDialog) {
      setShowAccountNotFoundDialog(true);
      globalShowDialog = false;
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('=== LOGIN ATTEMPT STARTED ===');
    console.log('Email:', email);
    setError('');
    setLoading(true);

    let shouldShowDialog = false;
    let errorMessage = '';

    try {
      console.log('Calling signIn function...');
      await signIn(email, password);
      console.log('Sign-in completed successfully');
    } catch (error: any) {
      // Log error in multiple ways for Capacitor compatibility
      console.error('LoginForm: Sign-in error caught:', error);
      console.error('LoginForm: Error type:', typeof error);
      console.error('LoginForm: Error string:', String(error));
      console.error('LoginForm: Error JSON:', JSON.stringify(error, null, 2));
      
      // Log individual error properties separately for Capacitor
      if (error) {
        console.error('LoginForm: Error.message:', error?.message || 'NO MESSAGE');
        console.error('LoginForm: Error.code:', error?.code || 'NO CODE');
        console.error('LoginForm: Error.name:', error?.name || 'NO NAME');
        console.error('LoginForm: Error.toString():', error?.toString() || 'NO STRING');
      }
      
      // Check for user not found errors - Firebase can return different error codes
      const errorCode = error?.code || error?.error?.code || '';
      const extractedErrorMessage = error?.message || error?.error?.message || error?.toString() || '';
      
      console.log('LoginForm: Error code:', errorCode);
      console.log('LoginForm: Error message:', extractedErrorMessage);
      
      const isUserNotFound = 
        errorCode === 'auth/user-not-found' ||
        errorCode === 'auth/invalid-credential' ||
        extractedErrorMessage.includes('user-not-found') ||
        extractedErrorMessage.includes('invalid-credential') ||
        extractedErrorMessage.includes('There is no user record corresponding to this identifier') ||
        extractedErrorMessage.includes('INVALID_LOGIN_CREDENTIALS') ||
        extractedErrorMessage.includes('EMAIL_NOT_FOUND');
      
      if (isUserNotFound) {
        shouldShowDialog = true;
      } else {
        // Always set error message - don't leave it empty
        errorMessage = extractedErrorMessage || 'Failed to sign in. Please try again.';
        console.log('LoginForm: Setting error message:', errorMessage);
      }
    } finally {
      console.log('LoginForm: Finally block - setting loading to false');
      setLoading(false);
      
      // Handle dialog and error display after loading is set to false
      if (shouldShowDialog) {
        console.log('LoginForm: Showing account not found dialog');
        globalShowDialog = true;
      } else if (errorMessage) {
        console.log('LoginForm: Setting error state:', errorMessage);
        setError(errorMessage);
      } else {
        console.warn('LoginForm: No error message to display!');
      }
    }
  };

  const handleGoogleSignIn = async () => {
    console.log('🔴🔴🔴 handleGoogleSignIn CALLED - BUTTON WAS CLICKED 🔴🔴🔴');
    console.log('🔴🔴🔴 This log should appear IMMEDIATELY when button is clicked 🔴🔴🔴');
    
    // Force flush logs
    if (console.log.toString().includes('native')) {
      console.log('🔴 Native console detected');
    }
    
    setError('');
    setLoading(true);

    try {
      const platform = Capacitor.getPlatform();
      console.log('🔴 Platform detected:', platform);
      console.log('🔴 About to call signInWithGoogle()...');
      
      if (platform === 'ios') {
        // iOS - show error (native not working)
        throw new Error('Google Sign-In is unavailable on iOS. Please use email/password sign-in.');
      } else if (platform === 'android') {
        // Android - use native plugin
        console.log('🔴 Android detected - calling signInWithGoogle() which should use native plugin');
        await signInWithGoogle();
        console.log('🔴 signInWithGoogle() completed');
      } else {
        // Web - use popup with timeout
        console.log('🔴 Web platform - using popup with timeout');
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Popup timeout - trying redirect instead')), 10000);
        });
        
        await Promise.race([
          signInWithGoogle(),
          timeoutPromise
        ]);
      }
    } catch (error: any) {
      console.error('🔴 Google Sign-In failed:', error);
      console.error('🔴 Error details:', {
        message: error?.message,
        code: error?.code,
        name: error?.name
      });
      
      const platform = Capacitor.getPlatform();
      if (platform === 'web') {
        // On web, try redirect as fallback
        if (error.message?.includes('popup') || 
            error.message?.includes('Cross-Origin') || 
            error.message?.includes('blocked') ||
            error.message?.includes('timeout') ||
            error.message?.includes('cancelled')) {
          try {
            setError('Popup blocked or timed out. Redirecting to Google sign-in...');
            await signInWithGoogleRedirect();
            return;
          } catch (redirectError: any) {
            setError(redirectError.message || 'Failed to sign in with Google');
          }
        } else {
          setError(error.message || 'Failed to sign in with Google');
        }
      } else if (platform === 'ios') {
        setError(error.message || 'Failed to sign in with Google. Please use email/password sign-in.');
      } else {
        // Android - show the error
        setError(error.message || 'Failed to sign in with Google. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ 
      p: { xs: 3, sm: 4 }, 
      maxWidth: 400, 
      mx: 'auto', 
      mt: { xs: 2, sm: 4 },
      width: '100%'
    }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Sign In
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          margin="normal"
          required
          disabled={loading}
        />
        
        <TextField
          fullWidth
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          margin="normal"
          required
          disabled={loading}
        />
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Link
            component="button"
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setShowForgotPasswordDialog(true);
            }}
            disabled={loading}
            sx={{ 
              textDecoration: 'underline', 
              cursor: 'pointer', 
              fontSize: '0.875rem',
              '&:disabled': {
                cursor: 'not-allowed',
                opacity: 0.5
              }
            }}
          >
            Forgot password?
          </Link>
        </Box>
        
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Sign In'}
        </Button>

        {/* Google Sign-In enabled for web only, disabled on iOS and Android */}
        {isWeb && (
          <>
            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" color="text.secondary">
                OR
              </Typography>
            </Divider>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<GoogleIcon />}
              onClick={(e) => {
                console.log('🔴🔴🔴 BUTTON ONCLICK EVENT FIRED 🔴🔴🔴');
                console.log('🔴 Event type:', e.type);
                console.log('🔴 Event target:', e.target);
                console.log('🔴 About to call handleGoogleSignIn...');
                e.preventDefault();
                e.stopPropagation();
                handleGoogleSignIn();
              }}
              disabled={loading}
              sx={{ mb: 2 }}
            >
              Sign in with Google
            </Button>
          </>
        )}
        
        <Button
          fullWidth
          variant="text"
          onClick={onToggleMode}
          disabled={loading}
        >
          Don't have an account? Sign up
        </Button>
      </Box>

      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          By signing in, you agree to our{' '}
          <Link
            component="button"
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setShowTermsDialog(true);
            }}
            sx={{ textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit' }}
          >
            Terms of Service
          </Link>
          {' '}and{' '}
          <Link
            component="button"
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setShowPrivacyDialog(true);
            }}
            sx={{ textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit' }}
          >
            Privacy Policy
          </Link>
        </Typography>
      </Box>

      <Box sx={{ textAlign: 'center', mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          <Link
            component="button"
            type="button"
            onClick={(e) => {
              e.preventDefault();
              navigate('/help');
            }}
            sx={{ textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit' }}
          >
            Need help?
          </Link>
        </Typography>
      </Box>

      <AccountNotFoundDialog
        open={showAccountNotFoundDialog}
        onClose={() => setShowAccountNotFoundDialog(false)}
        onCreateAccount={onToggleMode}
        onTryAgain={() => setShowAccountNotFoundDialog(false)}
      />

      <TermsOfServiceDialog
        open={showTermsDialog}
        onClose={() => setShowTermsDialog(false)}
      />

      <PrivacyPolicyDialog
        open={showPrivacyDialog}
        onClose={() => setShowPrivacyDialog(false)}
      />

      <ForgotPasswordDialog
        open={showForgotPasswordDialog}
        onClose={() => setShowForgotPasswordDialog(false)}
      />
    </Paper>
  );
};

export default LoginForm;
